import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Globe,
  HelpCircle,
  History,
  Link2,
  Loader2,
  Mail,
  Maximize2,
  Mic,
  Paperclip,
  Plus,
  Reply,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import {
  formatDuration,
  formatRelativeDate,
  formatShortDate,
  type PaginatedResponse,
  type Task,
  type TimeEntry,
} from '@oneup/shared';
import {
  tasksApi,
  timeEntriesApi,
  activityApi,
  aiQuickCaptureApi,
  ApiClientError,
  clientsApi,
  communicationsApi,
  websitesApi,
  type AiQuickCaptureProcessResponse,
  type CommunicationTimelineItem,
} from '@/lib/api';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MarkdownNotes } from '@/components/markdown-notes';
import { AiConfirmCard } from '@/components/ai-confirm-card';
import { SendUpdateModal } from '@/components/send-update-modal';
import { TaskTagsControl } from '@/components/task-tags-control';
import { RecordDetailShell } from '@/components/workspace';
import { useFieldOptions, type FieldOption } from '@/lib/use-field-options';
import { pickImageDataUrls, prepareFilesForUpload } from '@/lib/file-uploads';
import { cn } from '@/lib/utils';
import { formatAiError } from '@/lib/ai-errors';
import { pushRecent } from '@/lib/recents';
import { useUiStore } from '@/stores/ui';
import { taskStatusVariant } from '@/lib/task-status';
import { ClientDrawer, WebsiteDrawer } from './entity-drawers';

interface TaskDrawerProps {
  taskId: string;
  onClose?: () => void;
}

interface TaskDetailWorkspaceProps extends TaskDrawerProps {
  listTitle?: string;
  selectedIndex?: number;
  totalRecords?: number;
  onPrevious?: () => void;
  onNext?: () => void;
}

const TASKS_LIST_KEY = ['tasks', 'all'] as const;
const TASK_DETAIL_KEY = (id: string) => ['tasks', 'detail', id] as const;
const TIME_ENTRIES_KEY = (id: string) => ['time-entries', 'by-task', id] as const;
const TASK_ACTIVITY_KEY = (id: string) => ['activity', 'task', id] as const;

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
}

interface SpeechRecognitionResultEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

/** Quick-log button increments (in minutes). */
const QUICK_LOG_BUTTONS: Array<{ minutes: number; label: string; tone: 'primary' | 'default' }> = [
  { minutes: 60, label: '1 Hour', tone: 'primary' },
  { minutes: 12, label: '12m', tone: 'default' },
  { minutes: 24, label: '24m', tone: 'default' },
  { minutes: 36, label: '36m', tone: 'default' },
  { minutes: 48, label: '48m', tone: 'default' },
];

/**
 * TaskDrawer — Phase B + Phase C.
 *
 * Right-side Sheet that opens when the URL is `/tasks/:id`. Reads the task
 * from the list cache instantly and refetches detail in the background.
 * Phase C adds:
 *   - Tabs: Overview / Time / Linked / Activity
 *   - Quick-log buttons in the Time tab (1h / 12m / 24m / 36m / 48m)
   *   - Question for Clients editor + Send Update wired to the Make Orchestrator
 *   - Next Action / Definition of Done now persist to Airtable (no more
 *     "saved locally" badge)
 */
export function TaskDrawer({ taskId, onClose }: TaskDrawerProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: TASK_DETAIL_KEY(taskId),
    queryFn: () => tasksApi.get(taskId),
    initialData: () => {
      const list = queryClient.getQueryData<PaginatedResponse<Task>>(TASKS_LIST_KEY);
      return list?.items.find((t) => t.id === taskId);
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(TASKS_LIST_KEY)?.dataUpdatedAt ?? 0,
    staleTime: 10_000,
  });

  const timeEntriesQuery = useQuery({
    queryKey: TIME_ENTRIES_KEY(taskId),
    queryFn: () => timeEntriesApi.listByTask(taskId),
    staleTime: 30_000,
    enabled: Boolean(taskId),
  });

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    const qs = searchParams.toString();
    navigate(qs ? `/tasks?${qs}` : '/tasks', { replace: true });
  }, [navigate, onClose, searchParams]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleClose();
    },
    [handleClose],
  );

  return (
    <Sheet open onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] md:max-w-[720px]">
        {detailQuery.data ? (
          <TaskDetailView task={detailQuery.data} timeEntries={timeEntriesQuery.data?.items ?? []} />
        ) : detailQuery.isError ? (
          <DrawerError error={detailQuery.error} onClose={handleClose} />
        ) : (
          <DrawerSkeleton />
        )}
      </SheetContent>
    </Sheet>
  );
}

export function TaskDetailWorkspace({
  taskId,
  onClose,
  listTitle = 'Tasks',
  selectedIndex,
  totalRecords,
  onPrevious,
  onNext,
}: TaskDetailWorkspaceProps) {
  const queryClient = useQueryClient();
  const detailQuery = useQuery({
    queryKey: TASK_DETAIL_KEY(taskId),
    queryFn: () => tasksApi.get(taskId),
    initialData: () => {
      const list = queryClient.getQueryData<PaginatedResponse<Task>>(TASKS_LIST_KEY);
      return list?.items.find((t) => t.id === taskId);
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(TASKS_LIST_KEY)?.dataUpdatedAt ?? 0,
    staleTime: 10_000,
  });

  const timeEntriesQuery = useQuery({
    queryKey: TIME_ENTRIES_KEY(taskId),
    queryFn: () => timeEntriesApi.listByTask(taskId),
    staleTime: 30_000,
    enabled: Boolean(taskId),
  });

  return (
    <RecordDetailShell
      title={detailQuery.data?.name ?? 'Task'}
      recordType={listTitle}
      onClose={onClose ?? (() => undefined)}
      canGoPrevious={typeof selectedIndex === 'number' && selectedIndex > 0}
      canGoNext={
        typeof selectedIndex === 'number' &&
        typeof totalRecords === 'number' &&
        selectedIndex >= 0 &&
        selectedIndex < totalRecords - 1
      }
      onPrevious={onPrevious}
      onNext={onNext}
      className="min-h-[640px]"
    >
      {detailQuery.data ? (
        <TaskDetailView
          task={detailQuery.data}
          timeEntries={timeEntriesQuery.data?.items ?? []}
          surface="workspace"
        />
      ) : detailQuery.isError ? (
        <div className="p-5 text-sm text-destructive">
          {detailQuery.error instanceof Error ? detailQuery.error.message : 'Could not load task.'}
        </div>
      ) : (
        <DrawerSkeleton />
      )}
    </RecordDetailShell>
  );
}

// ---------- Detail body ----------

interface TaskDetailViewProps {
  task: Task;
  timeEntries: TimeEntry[];
  surface?: 'drawer' | 'workspace';
  onClose?: () => void;
}

export function TaskDetailView({
  task,
  timeEntries,
  surface = 'drawer',
  onClose,
}: TaskDetailViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const setActiveDrawer = useUiStore((state) => state.setActiveDrawer);
  const taskDrawerCommand = useUiStore((state) => state.taskDrawerCommand);
  const clearTaskDrawerCommand = useUiStore((state) => state.clearTaskDrawerCommand);
  const setNotice = useUiStore((state) => state.setNotice);
  const [showRawNotes, setShowRawNotes] = useState(false);
  const [expandedNotesEditing, setExpandedNotesEditing] = useState(false);
  // Seed value used to pre-populate the expanded notes editor with an
  // AI-generated recap draft. Cleared whenever the editor closes (save or
  // cancel) so subsequent opens go back to showing the persisted notes.
  const [contextDump, setContextDump] = useState('');
  const [contextFiles, setContextFiles] = useState<string[]>([]);
  const [contextFileUrls, setContextFileUrls] = useState<string[]>([]);
  const [voiceStatus, setVoiceStatus] = useState<string | undefined>();
  const [sendStatus, setSendStatus] = useState<
    | { state: 'idle' }
    | { state: 'sending' }
    | { state: 'sent'; at: number }
    | { state: 'error'; message: string }
  >({ state: 'idle' });
  // Pass 3 — Quick Capture AI proposal (the result of /process), gated until
  // the user confirms or discards via the confirm-card. Only field changes /
  // new client questions ever sit here; the auto-appended notes already
  // landed in Airtable when /process returned.
  const [aiProposal, setAiProposal] = useState<AiQuickCaptureProcessResponse | null>(null);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  // Send Update modal — opened by the red Send Update button (the keyboard
  // shortcut "U" opens this same review flow.
  const [sendUpdateOpen, setSendUpdateOpen] = useState(false);

  const patchMutation = useMutation({
    mutationFn: (patch: Partial<Task>) => tasksApi.patch(task.id, patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: TASKS_LIST_KEY });
      await queryClient.cancelQueries({ queryKey: TASK_DETAIL_KEY(task.id) });

      const previousList = queryClient.getQueryData<PaginatedResponse<Task>>(TASKS_LIST_KEY);
      const previousDetail = queryClient.getQueryData<Task>(TASK_DETAIL_KEY(task.id));

      const optimistic = mergeTaskPatch(task, patch);

      if (previousList) {
        queryClient.setQueryData<PaginatedResponse<Task>>(TASKS_LIST_KEY, {
          ...previousList,
          items: previousList.items.map((t) => (t.id === task.id ? optimistic : t)),
        });
      }
      queryClient.setQueryData<Task>(TASK_DETAIL_KEY(task.id), optimistic);

      return { previousList, previousDetail };
    },
    onError: (_err, _patch, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(TASKS_LIST_KEY, context.previousList);
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(TASK_DETAIL_KEY(task.id), context.previousDetail);
      }
    },
    onSuccess: (_data, patch) => {
      const action = actionForPatch(patch);
      if (action) {
        activityApi
          .log({
            entityType: 'task',
            entityId: task.id,
            entityLabel: task.name,
            action,
            newValue: patch,
            source: 'portal',
          })
          .then(() => queryClient.invalidateQueries({ queryKey: TASK_ACTIVITY_KEY(task.id) }))
          .catch(() => undefined);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASKS_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: TASK_DETAIL_KEY(task.id) });
      queryClient.invalidateQueries({
        queryKey: ['schema', 'field-options', 'ENT_Projects', 'Command Center Flags'],
      });
    },
  });

  const patchTask = useCallback(
    (patch: Partial<Task>) => patchMutation.mutate(patch),
    [patchMutation],
  );

  const setCommandCenterFlag = useCallback(
    (flag: string, enabled: boolean) => {
      const current = task.commandCenterFlags ?? [];
      const next = enabled
        ? current.includes(flag)
          ? current
          : [...current, flag]
        : current.filter((item) => item !== flag);
      if (next !== current) patchTask({ commandCenterFlags: next });
    },
    [patchTask, task.commandCenterFlags],
  );

  const openSendUpdate = useCallback(() => {
    setCommandCenterFlag('Send To Client', true);
    setSendUpdateOpen(true);
  }, [setCommandCenterFlag]);

  const quickLogMutation = useMutation({
    mutationFn: (minutes: number) => {
      const endedAt = new Date();
      const startedAt = new Date(endedAt.getTime() - minutes * 60_000);
      return timeEntriesApi.create({
        taskId: task.id,
        taskName: task.name,
        clientId: task.client?.id,
        notes: `Quick log: ${minutes} min`,
        billable: !(task.client?.isInternal ?? false),
        durationMinutes: minutes,
        startedAt: startedAt.toISOString(),
        endedAt: endedAt.toISOString(),
      });
    },
    onSuccess: (created, minutes) => {
      // Optimistically prepend to the time entries list while the refetch lands.
      const previous = queryClient.getQueryData<PaginatedResponse<TimeEntry>>(
        TIME_ENTRIES_KEY(task.id),
      );
      if (previous) {
        queryClient.setQueryData<PaginatedResponse<TimeEntry>>(TIME_ENTRIES_KEY(task.id), {
          ...previous,
          items: [created, ...previous.items],
          total: (previous.total ?? previous.items.length) + 1,
        });
      }
      activityApi
        .log({
          entityType: 'time_entry',
          entityId: created.id,
          entityLabel: `${minutes}m on ${task.name}`,
          action: 'time_logged',
          newValue: { minutes },
          notes: `Quick log via portal (+${minutes}m)`,
          source: 'portal',
        })
        .then(() => queryClient.invalidateQueries({ queryKey: TASK_ACTIVITY_KEY(task.id) }))
        .catch(() => undefined);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TIME_ENTRIES_KEY(task.id) });
      // Tracked-time roll-ups update on the task too, so refetch detail.
      queryClient.invalidateQueries({ queryKey: TASK_DETAIL_KEY(task.id) });
    },
  });

  useEffect(() => {
    setActiveDrawer({ type: 'task', id: task.id, name: task.name });
    pushRecent({
      type: 'task',
      id: task.id,
      name: task.name,
      secondary: task.client?.name ?? task.status,
    });
    return () => setActiveDrawer(null);
  }, [setActiveDrawer, task.client?.name, task.id, task.name, task.status]);

  useEffect(() => {
    if (!taskDrawerCommand || taskDrawerCommand.nonce === 0) return;
    const { command, nonce } = taskDrawerCommand;
    clearTaskDrawerCommand(nonce);
    if (command === 'send-update') {
      setSendUpdateOpen(true);
      return;
    }
    if (command === 'focus-edit') {
      rootRef.current
        ?.querySelector<HTMLTextAreaElement | HTMLInputElement>('[data-task-editable="true"]')
        ?.focus();
      return;
    }
    if (command === 'quick-log') {
      rootRef.current?.querySelector<HTMLButtonElement>('[data-quick-log="true"]')?.focus();
      setNotice('Quick Log focused');
      return;
    }
    if (command === 'close-task') {
      setNotice('Close Task checklist is queued for the next pass');
    }
  }, [clearTaskDrawerCommand, setNotice, taskDrawerCommand]);

  // Auto-clear the "sent" toast after 4s.
  useEffect(() => {
    if (sendStatus.state !== 'sent') return;
    const handle = setTimeout(() => setSendStatus({ state: 'idle' }), 4000);
    return () => clearTimeout(handle);
  }, [sendStatus]);

  const totalTrackedFromEntries = useMemo(
    () => timeEntries.reduce((sum, e) => sum + (e.durationMinutes ?? 0), 0),
    [timeEntries],
  );

  const addContextDump = useCallback(() => {
    const trimmed = contextDump.trim();
    if (!trimmed && contextFiles.length === 0) return;
    patchMutation.mutate({
      notes: appendContextDump(task.notes ?? '', trimmed, contextFiles),
    });
    setContextDump('');
    setContextFiles([]);
    setContextFileUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [contextDump, contextFiles, patchMutation, task.notes]);

  /**
   * Pass 3 — "Process AI" path.
   *
   * Sends the captured text/voice transcript to /quick-capture/process. The
   * Worker auto-appends AI-suggested notes to Airtable (server-side) and
   * returns any field-change suggestions / new client questions for the
   * user to explicitly confirm via <AiConfirmCard>.
   *
   * If AI is down or the key is missing, surface a clear error and let the
   * user fall back to "Append note" — Notes append never depends
   * on AI being available.
   *
   * Note on file handling: we send image data URLs to AI for vision/context
   * processing, but do not persist those uploads into Airtable attachments in
   * this flow. Airtable requires hosted/public URLs for attachment fields.
   */
  const processWithAi = useCallback(async () => {
    const trimmed = contextDump.trim();
    const sanitizedFileUrls = contextFileUrls.filter((url) => typeof url === 'string' && url.trim().length > 0);
    const payload = {
      text: trimmed || undefined,
      fileUrls: sanitizedFileUrls.length > 0 ? sanitizedFileUrls : undefined,
    };

    if (!payload.text && !payload.fileUrls?.length) {
      setAiError('Add text/voice context or attach an image file before running AI processing.');
      return;
    }

    setAiProcessing(true);
    setAiError(null);
    try {
      const result = await aiQuickCaptureApi.process(task.id, payload);
      // Refetch the task so the auto-appended notes show up in the drawer.
      queryClient.invalidateQueries({ queryKey: TASK_DETAIL_KEY(task.id) });
      setContextDump('');
      setContextFiles([]);
      setContextFileUrls([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setAiProposal(result);
    } catch (err) {
      setAiError(formatAiError(err, 'AI processing failed'));
    } finally {
      setAiProcessing(false);
    }
  }, [contextDump, contextFileUrls, queryClient, task.id]);

  const onAiProposalResolved = useCallback(() => {
    setAiProposal(null);
    setAiError(null);
    queryClient.invalidateQueries({ queryKey: TASK_DETAIL_KEY(task.id) });
  }, [queryClient, task.id]);

  const startVoiceEntry = useCallback(() => {
    const SpeechRecognitionCtor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      setVoiceStatus('Voice entry is not supported in this browser.');
      return;
    }

    setVoiceStatus('Listening...');
    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript;
      if (transcript) setContextDump((value) => `${value}${value ? '\n' : ''}${transcript}`);
      setVoiceStatus(undefined);
    };
    recognition.onerror = () => setVoiceStatus('Voice entry failed. Try again.');
    recognition.onend = () => {
      setVoiceStatus((status) => (status === 'Listening...' ? undefined : status));
    };
    recognition.start();
  }, []);

  const handleFilesSelected = useCallback(async (files: File[]) => {
    const names: string[] = [];
    const prepared: Awaited<ReturnType<typeof prepareFilesForUpload>> = [];
    const snippets: string[] = [];
    const failures: string[] = [];
    const resized: string[] = [];

    for (const file of files) {
      names.push(file.name);
      try {
        const result = await prepareFilesForUpload([file]);
        if (result[0]) {
          prepared.push(result[0]);
          if (result[0].resized) resized.push(file.name);
        }
      } catch {
        failures.push(file.name);
      }

      if (file.type.startsWith('text/') || /\.(csv|json|md|txt)$/i.test(file.name)) {
        try {
          const text = await file.text();
          snippets.push(`File: ${file.name}\n${text.slice(0, 10_000)}`);
        } catch {
          failures.push(file.name);
        }
      }
    }

    setContextFiles(names);
    setContextFileUrls(pickImageDataUrls(prepared));
    if (snippets.length > 0) {
      setContextDump((value) => `${value}${value ? '\n\n' : ''}${snippets.join('\n\n')}`);
    }
    if (failures.length > 0) {
      setAiError(`Could not process ${failures.length} file(s): ${Array.from(new Set(failures)).join(', ')}`);
    } else if (resized.length > 0) {
      // Soft notice — resize succeeded, just letting the operator know vision
      // input was downscaled. Doesn't block submit.
      setVoiceStatus(
        `Image${resized.length > 1 ? 's' : ''} resized for AI: ${resized.join(', ')}`,
      );
    }
  }, []);

  if (expandedNotesEditing) {
    return (
      <ExpandedNotesEditor
        taskName={task.name}
        initialValue={task.notes ?? ''}
        surface={surface}
        savePending={patchMutation.isPending}
        onSave={(value) => {
          patchMutation.mutate({ notes: value });
          setExpandedNotesEditing(false);
        }}
        onCancel={() => {
          setExpandedNotesEditing(false);
        }}
      />
    );
  }

  return (
    <>
      {surface === 'workspace' && (
        <TaskDetailHeader
          task={task}
          surface={surface}
          patchPending={patchMutation.isPending}
          patchError={patchMutation.isError ? patchMutation.error : undefined}
          onPatchFlags={(commandCenterFlags) => patchTask({ commandCenterFlags })}
          onClose={onClose}
        />
      )}
      {surface === 'drawer' && (
      <SheetHeader>
        <div className="flex flex-col gap-3 pr-6 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1">
            <SheetTitle className="break-words">{task.name}</SheetTitle>
            <SheetDescription className="mt-1 flex flex-wrap items-center gap-2">
              {task.client?.name && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3 w-3" aria-hidden />
                  {task.client.name}
                </span>
              )}
              {task.statusLastModified && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" aria-hidden />
                  Updated {formatRelativeDate(task.statusLastModified)}
                </span>
              )}
              {patchMutation.isPending && (
                <span className="inline-flex items-center gap-1 text-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  saving…
                </span>
              )}
              {patchMutation.isError && (
                <span className="inline-flex items-center gap-1 text-destructive">
                  <AlertCircle className="h-3 w-3" aria-hidden />
                  Save failed —{' '}
                  {patchMutation.error instanceof ApiClientError
                    ? patchMutation.error.message
                    : 'try again'}
                </span>
              )}
            </SheetDescription>
          </div>
          <div className="w-full sm:max-w-[280px]">
            <TaskTagsControl
              value={task.commandCenterFlags}
              disabled={patchMutation.isPending}
              onChange={(commandCenterFlags) => patchTask({ commandCenterFlags })}
            />
          </div>
        </div>
      </SheetHeader>
      )}

      <div
        ref={rootRef}
        className={cn(
          'text-[13px]',
          surface === 'drawer' ? 'flex-1 overflow-y-auto px-5 py-4' : 'px-4 py-4',
        )}
      >
        <TaskStatusBar
          task={task}
          patchPending={patchMutation.isPending}
          sendStatus={sendStatus}
          onPatch={patchTask}
          onSendUpdate={openSendUpdate}
          onEditNotes={() => setExpandedNotesEditing(true)}
        />

        <ActionCenter
          fileInputRef={fileInputRef}
          task={task}
          contextDump={contextDump}
          contextFiles={contextFiles}
          voiceStatus={voiceStatus}
          quickLogPending={quickLogMutation.isPending}
          quickLogError={
            quickLogMutation.isError
              ? quickLogMutation.error instanceof ApiClientError
                ? quickLogMutation.error.message
                : 'Log failed'
              : undefined
          }
          onContextChange={setContextDump}
          onFilesSelected={handleFilesSelected}
          onClearFiles={() => {
            setContextFiles([]);
            setContextFileUrls([]);
          }}
          onVoiceEntry={startVoiceEntry}
          onAddContext={addContextDump}
          onProcessAi={processWithAi}
          aiProcessing={aiProcessing}
          aiProposal={aiProposal}
          aiError={aiError}
          onAiProposalResolved={onAiProposalResolved}
          onQuickLog={(minutes) => quickLogMutation.mutate(minutes)}
        />

        <Tabs defaultValue="overview" className="flex flex-col gap-3">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="time">
              Time
              {timeEntries.length > 0 && (
                <span className="ml-0.5 rounded-full bg-muted px-1.5 py-0 text-[10px] font-medium tabular-nums text-muted-foreground">
                  {timeEntries.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="linked">Linked</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-5">
            <OverviewCard overview={task.overview} isStale={task.overviewIsStale} />

            <CriticalFields task={task} onPatch={patchTask} />

            <TaskHierarchyBlock task={task} onOpenTask={(id) => navigate(`/tasks/${id}`)} />

            <div>
              <SectionHeading>Next Action</SectionHeading>
              <AirtableTextField
                value={task.nextAction ?? ''}
                placeholder="What's the very next physical step on this task?"
                onSave={(v) => patchMutation.mutate({ nextAction: v })}
              />
            </div>

            <div>
              <SectionHeading>Definition of Done</SectionHeading>
              <AirtableTextField
                value={task.definitionOfDone ?? ''}
                placeholder="What does 'done' look like? (acceptance criteria, deliverables, sign-off needed)"
                onSave={(v) => patchMutation.mutate({ definitionOfDone: v })}
                rows={3}
              />
            </div>

            <div>
              <SectionHeading>Screenshots</SectionHeading>
              <ScreenshotUrlsField
                value={task.screenshotUrls ?? []}
                onSave={(urls) => patchMutation.mutate({ screenshotUrls: urls })}
              />
            </div>

            <ClientCommsBlock
              task={task}
              sendStatus={sendStatus}
              onSaveQuestions={(v) => patchMutation.mutate({ questionForClients: v })}
            />

            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <SectionHeading className="mb-0">Notes</SectionHeading>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRawNotes((v) => !v)}
                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {showRawNotes ? (
                      <>
                        <Eye className="h-3 w-3" aria-hidden />
                        Preview
                      </>
                    ) : (
                      <>
                        <EyeOff className="h-3 w-3" aria-hidden />
                        Edit raw
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpandedNotesEditing(true)}
                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                    title="Open full-drawer editor (double-click notes to do the same)"
                  >
                    <Maximize2 className="h-3 w-3" aria-hidden />
                    Expand
                  </button>
                </div>
              </div>
              <NotesField
                value={task.notes ?? ''}
                raw={showRawNotes}
                onSave={(v) => patchMutation.mutate({ notes: v })}
                onExpandedEdit={() => setExpandedNotesEditing(true)}
              />
            </div>
          </TabsContent>

          {/* TIME */}
          <TabsContent value="time" className="space-y-4">
            <TimeSummary task={task} entriesTotalMinutes={totalTrackedFromEntries} />

            <div>
              <SectionHeading>Time Entries</SectionHeading>
              <TimeEntriesList entries={timeEntries} />
            </div>
          </TabsContent>

          {/* LINKED */}
          <TabsContent value="linked" className="space-y-3">
            <SectionHeading>Linked records</SectionHeading>
            <LinkedRecords task={task} />
            <SectionHeading>Linked emails</SectionHeading>
            <TaskLinkedEmails taskId={task.id} />
          </TabsContent>

          {/* ACTIVITY */}
          <TabsContent value="activity">
            <SectionHeading>Activity</SectionHeading>
            <TaskActivityFeed taskId={task.id} />
          </TabsContent>
        </Tabs>
      </div>
      <SendUpdateModal
        task={task}
        open={sendUpdateOpen}
        onOpenChange={setSendUpdateOpen}
        onSent={() => {
          setSendStatus({ state: 'sent', at: Date.now() });
          setCommandCenterFlag('Send To Client', false);
          activityApi
            .log({
              entityType: 'task',
              entityId: task.id,
              entityLabel: task.name,
              action: 'client_update_sent',
              newValue: { aiDrafted: true },
              source: 'portal',
            })
            .then(() => queryClient.invalidateQueries({ queryKey: TASK_ACTIVITY_KEY(task.id) }))
            .catch(() => undefined);
        }}
      />
    </>
  );
}

function TaskDetailHeader({
  task,
  surface,
  patchPending,
  patchError,
  onPatchFlags,
  onClose,
}: {
  task: Task;
  surface: 'drawer' | 'workspace';
  patchPending: boolean;
  patchError?: unknown;
  onPatchFlags: (commandCenterFlags: string[]) => void;
  onClose?: () => void;
}) {
  return (
    <div
      className={cn(
        'border-b border-border px-4 py-4',
        surface === 'workspace' && 'bg-background/40',
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <h2 className="break-words text-[15px] font-semibold leading-tight">{task.name}</h2>
            {onClose && (
              <Button type="button" size="sm" variant="outline" className="h-7" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
            {task.client?.name && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="h-3 w-3" aria-hidden />
                {task.client.name}
              </span>
            )}
            {task.statusLastModified && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" aria-hidden />
                Updated {formatRelativeDate(task.statusLastModified)}
              </span>
            )}
            {patchPending && (
              <span className="inline-flex items-center gap-1 text-foreground">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                saving...
              </span>
            )}
            {Boolean(patchError) && (
              <span className="inline-flex items-center gap-1 text-destructive">
                <AlertCircle className="h-3 w-3" aria-hidden />
                Save failed:{' '}
                {patchError instanceof ApiClientError ? patchError.message : 'try again'}
              </span>
            )}
          </div>
        </div>
        <div className="w-full sm:max-w-[280px]">
          <TaskTagsControl
            value={task.commandCenterFlags}
            disabled={patchPending}
            onChange={onPatchFlags}
          />
        </div>
      </div>
    </div>
  );
}

function TaskStatusBar({
  task,
  patchPending,
  sendStatus,
  onPatch,
  onSendUpdate,
  onEditNotes,
}: {
  task: Task;
  patchPending: boolean;
  sendStatus:
    | { state: 'idle' }
    | { state: 'sending' }
    | { state: 'sent'; at: number }
    | { state: 'error'; message: string };
  onPatch: (patch: Partial<Task>) => void;
  onSendUpdate: () => void;
  onEditNotes: () => void;
}) {
  const hasQuestions = Boolean((task.questionForClients ?? '').trim());

  return (
    <div className="mb-3 flex flex-col gap-2 border-y border-border/70 bg-background/60 px-1 py-2 text-[12px] md:flex-row md:items-center md:justify-between">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <CompactStatusField label="Status" className="w-[168px]">
          <SchemaSelect
            field="Status"
            value={task.status}
            fallbackOptions={['Open', 'In Progress', 'Waiting on Client', 'Review', 'Closed']}
            triggerClassName="h-7 border-border/80 bg-card px-2 text-[12px]"
            onChange={(value) => onPatch({ status: value })}
          />
        </CompactStatusField>
        <CompactStatusField label="Due" className="w-[142px]">
          <DateField
            value={task.targetDate}
            inputClassName="h-7 border-border/80 bg-card px-2 text-[12px]"
            onChange={(value) => onPatch({ targetDate: value })}
          />
        </CompactStatusField>
        <div className="inline-flex h-7 items-center gap-1.5 rounded-md border border-transparent px-1.5">
          <span className="font-semibold text-muted-foreground">Tracked</span>
          <span className="font-medium tabular-nums text-foreground">
            {formatDuration(task.trackedMinutes)}
          </span>
        </div>
        {patchPending && (
          <span className="inline-flex h-7 items-center gap-1 text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            saving
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 md:justify-end">
        <Button
          type="button"
          size="sm"
          onClick={onSendUpdate}
          disabled={sendStatus.state === 'sending'}
          className="h-8 gap-1.5"
        >
          {sendStatus.state === 'sending' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Send className="h-3.5 w-3.5" aria-hidden />
          )}
          Send To Client
          {hasQuestions && (
            <span className="rounded-full bg-primary-foreground/20 px-1.5 py-0 text-[10px] font-semibold">
              ?
            </span>
          )}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5"
          onClick={onEditNotes}
        >
          <FileText className="h-3.5 w-3.5" aria-hidden />
          Edit Notes
        </Button>
        {sendStatus.state === 'sent' && (
          <span className="inline-flex items-center gap-1 text-[11px] text-success">
            <CheckCircle2 className="h-3 w-3" aria-hidden />
            Update queued
          </span>
        )}
        {sendStatus.state === 'error' && (
          <span className="inline-flex items-center gap-1 text-[11px] text-destructive">
            <AlertCircle className="h-3 w-3" aria-hidden />
            Send failed
          </span>
        )}
      </div>
    </div>
  );
}

function CompactStatusField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label
      className={cn(
        'grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-1.5',
        className,
      )}
    >
      <span className="font-semibold text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function TaskHierarchyBlock({
  task,
  onOpenTask,
}: {
  task: Task;
  onOpenTask: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const children = task.childTasks ?? [];
  if (!task.parentTaskId && children.length === 0) return null;

  return (
    <div className="space-y-2">
      {task.parentTaskId && (
        <button
          type="button"
          onClick={() => onOpenTask(task.parentTaskId!)}
          className="inline-flex max-w-full items-center gap-1.5 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronRight className="h-3.5 w-3.5 rotate-180" aria-hidden />
          <span className="truncate">Parent: {task.parentTaskName ?? task.parentTaskId}</span>
        </button>
      )}
      {children.length > 0 && (
        <div className="rounded-md border border-border">
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[12px] font-semibold text-foreground hover:bg-muted/40"
          >
            <span>Child Tasks ({children.length})</span>
            <ChevronRight
              className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', expanded && 'rotate-90')}
              aria-hidden
            />
          </button>
          {expanded && (
            <ul className="divide-y divide-border">
              {children.map((child) => (
                <li key={child.id}>
                  <button
                    type="button"
                    onClick={() => onOpenTask(child.id)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[13px] hover:bg-muted/30"
                  >
                    <span className="min-w-0 truncate font-medium">{child.name}</span>
                    {child.status && (
                      <Badge variant={taskStatusVariant(child.semanticStatus)}>
                        {child.status}
                      </Badge>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Critical fields ----------

function CriticalFields({
  task,
  onPatch,
}: {
  task: Task;
  onPatch: (patch: Partial<Task>) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
      <FieldRow label="Status">
        <SchemaSelect
          field="Status"
          value={task.status}
          fallbackOptions={['Open', 'In Progress', 'Waiting on Client', 'Review', 'Closed']}
          onChange={(value) => onPatch({ status: value })}
        />
      </FieldRow>
      <FieldRow label="Priority">
        <SchemaSelect
          field="Priority"
          value={task.priority ?? ''}
          fallbackOptions={['urgent', 'high', 'normal', 'low']}
          onChange={(value) => onPatch({ priority: value })}
        />
      </FieldRow>
      <FieldRow label="Target Date">
        <DateField value={task.targetDate} onChange={(value) => onPatch({ targetDate: value })} />
      </FieldRow>
      <FieldRow label="Estimate (min)">
        <NumberField
          value={task.estimateMinutes}
          onChange={(value) => onPatch({ estimateMinutes: value })}
          placeholder="—"
        />
      </FieldRow>
      <FieldRow label="Bill Status">
        <SchemaSelect
          field="billStatus"
          value={task.billStatus ?? ''}
          fallbackOptions={['Not Processed', 'Ready to Bill', 'Billed', 'Not Billable']}
          onChange={(value) => onPatch({ billStatus: value })}
        />
      </FieldRow>
      <FieldRow label="Final Review">
        <SchemaSelect
          field="Final Review"
          value={task.finalReview ?? ''}
          fallbackOptions={['Not Reviewed', 'In Review', 'Reviewed', 'Approved']}
          onChange={(value) => onPatch({ finalReview: value })}
        />
      </FieldRow>
      <FieldRow label="Tracked">
        <ReadOnlyValue>{formatDuration(task.trackedMinutes)}</ReadOnlyValue>
      </FieldRow>
      <FieldRow label="Week Tracked">
        <ReadOnlyValue>{formatDuration(task.weekTrackedMinutes)}</ReadOnlyValue>
      </FieldRow>
      <FieldRow label="Subtask Tracked">
        <ReadOnlyValue>{formatDuration(task.subtaskTrackedMinutes)}</ReadOnlyValue>
      </FieldRow>
      <FieldRow label="Total W Subtasks">
        <ReadOnlyValue>{formatDuration(task.totalTrackedWithSubtasksMinutes)}</ReadOnlyValue>
      </FieldRow>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function ReadOnlyValue({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-8 items-center px-1 text-[13px] text-muted-foreground">{children}</div>
  );
}

function ScreenshotUrlsField({
  value,
  onSave,
}: {
  value: string[];
  onSave: (urls: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addUrl = () => {
    const next = draft.trim();
    if (!next) return;
    try {
      const parsed = new URL(next);
      if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('invalid');
      onSave(Array.from(new Set([...value, parsed.toString()])));
      setDraft('');
      setError(null);
    } catch {
      setError('Enter a valid http(s) URL for the screenshot.');
    }
  };

  return (
    <Card className="space-y-2 px-3 py-3">
      <p className="text-[11px] text-muted-foreground">
        Screenshot attachments are managed separately from AI processing. Paste hosted image URLs.
      </p>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="https://..."
          className="h-9 w-full rounded-md border border-input bg-background px-2 text-[13px]"
        />
        <Button type="button" size="sm" className="h-9" onClick={addUrl}>
          Add
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="space-y-1">
        {value.length === 0 && <p className="text-xs text-muted-foreground">No screenshots attached.</p>}
        {value.map((url) => (
          <div key={url} className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="truncate text-xs text-primary underline underline-offset-2"
            >
              {url}
            </a>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onSave(value.filter((v) => v !== url))}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------- Action center ----------

interface ActionCenterProps {
  fileInputRef: RefObject<HTMLInputElement>;
  task: Task;
  contextDump: string;
  contextFiles: string[];
  voiceStatus?: string;
  quickLogPending: boolean;
  quickLogError?: string;
  onContextChange: (value: string) => void;
  onFilesSelected: (files: File[]) => void;
  onClearFiles: () => void;
  onVoiceEntry: () => void;
  onAddContext: () => void;
  /** Pass 3 — runs the captured input through Quick Capture AI. */
  onProcessAi: () => void;
  aiProcessing: boolean;
  aiProposal: AiQuickCaptureProcessResponse | null;
  aiError: string | null;
  onAiProposalResolved: () => void;
  onQuickLog: (minutes: number) => void;
}

/**
 * ActionCenter — the top-of-drawer action strip.
 *
 * Organized into the two high-frequency groups that need to stay in the
 * action center: quick time logging and quick notes capture.
 */
function ActionCenter({
  fileInputRef,
  task,
  contextDump,
  contextFiles,
  voiceStatus,
  quickLogPending,
  quickLogError,
  onContextChange,
  onFilesSelected,
  onClearFiles,
  onVoiceEntry,
  onAddContext,
  onProcessAi,
  aiProcessing,
  aiProposal,
  aiError,
  onAiProposalResolved,
  onQuickLog,
}: ActionCenterProps) {
  const canAddContext = contextDump.trim().length > 0 || contextFiles.length > 0;
  const quickCaptureRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => quickCaptureRef.current?.focus(), 0);
    return () => window.clearTimeout(handle);
  }, [task.id]);

  return (
    <div className="mb-3 rounded-lg border border-border bg-muted/25 p-2.5 sm:p-3">
      <SectionHeading className="mb-2">Action Center</SectionHeading>

      {/* TIME — quick-log buttons only */}
      <ActionGroup label="Time">
        <div className="flex flex-wrap items-center gap-1.5">
          {QUICK_LOG_BUTTONS.map((btn) => (
            <Button
              key={btn.minutes}
              type="button"
              size="sm"
              variant={btn.tone === 'primary' ? 'default' : 'outline'}
              data-quick-log={btn.minutes === 60 ? 'true' : undefined}
              disabled={quickLogPending}
              onClick={() => onQuickLog(btn.minutes)}
              className={cn(
                'h-8 gap-1.5',
                btn.tone === 'primary' &&
                'bg-success text-white hover:bg-success/90 focus-visible:ring-success/40',
              )}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              {btn.label}
            </Button>
          ))}
          {quickLogPending && (
            <span className="inline-flex items-center gap-1 px-1 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              Logging...
            </span>
          )}
          {quickLogError && (
            <span className="inline-flex items-center gap-1 px-1 text-[11px] text-destructive">
              <AlertCircle className="h-3 w-3" aria-hidden />
              {quickLogError}
            </span>
          )}
        </div>
      </ActionGroup>

      {/* NOTES — edit entry + capture flow */}
      <ActionGroup label="Notes" last>
        <div className="space-y-1.5 sm:space-y-2">
          <textarea
            ref={quickCaptureRef}
            value={contextDump}
            onChange={(event) => onContextChange(event.target.value)}
            placeholder="Quick capture notes or client feedback…"
            rows={3}
            className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          {contextFiles.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground">Files:</span>
              {contextFiles.map((file) => (
                <span key={file} className="rounded-full bg-muted px-2 py-0.5">
                  {file}
                </span>
              ))}
              <button type="button" className="text-primary hover:underline" onClick={onClearFiles}>
                clear
              </button>
            </div>
          )}
          {voiceStatus && <div className="text-[11px] text-muted-foreground">{voiceStatus}</div>}
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-1.5">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(event) => onFilesSelected(Array.from(event.target.files ?? []))}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="h-3.5 w-3.5" aria-hidden />
                Files
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5"
                onClick={onVoiceEntry}
              >
                <Mic className="h-3.5 w-3.5" aria-hidden />
                Voice
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!canAddContext || aiProcessing}
                onClick={onAddContext}
                className="h-8"
              >
                Append note
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!canAddContext || aiProcessing}
                onClick={onProcessAi}
                className="h-8 gap-1.5"
              >
                {aiProcessing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                )}
                Process AI
              </Button>
            </div>
          </div>
          {aiError && (
            <div className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 text-[12px] text-destructive">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
              <span>{aiError}</span>
            </div>
          )}
          {aiProposal && (
            <AiConfirmCard
              taskId={task.id}
              proposal={aiProposal}
              onResolved={onAiProposalResolved}
            />
          )}
        </div>
      </ActionGroup>
    </div>
  );
}

/**
 * ActionGroup — small wrapper that gives each cluster a tiny uppercase label
 * and a subtle bottom border so the groups read as distinct without taking
 * extra vertical space.
 */
function ActionGroup({
  label,
  last,
  children,
}: {
  label: string;
  last?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('py-2', !last && 'border-b border-border/60')}>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.7px] text-muted-foreground/80">
        {label}
      </div>
      {children}
    </div>
  );
}

// ---------- Field editors ----------

interface SchemaSelectProps {
  field: string;
  value: string;
  fallbackOptions: string[];
  onChange: (value: string) => void;
  triggerClassName?: string;
}

function SchemaSelect({
  field,
  value,
  fallbackOptions,
  onChange,
  triggerClassName,
}: SchemaSelectProps) {
  const { data, isLoading } = useFieldOptions(field);

  const options = useMemo<FieldOption[]>(() => {
    const fromSchema = data?.options;
    if (fromSchema && fromSchema.length > 0) return fromSchema;
    return fallbackOptions.map((name) => ({ name }));
  }, [data?.options, fallbackOptions]);

  const allOptions = useMemo<FieldOption[]>(() => {
    if (!value) return options;
    if (options.some((o) => o.name === value)) return options;
    return [{ name: value }, ...options];
  }, [options, value]);

  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder={isLoading ? 'Loading…' : 'Select…'} />
      </SelectTrigger>
      <SelectContent>
        {allOptions.map((opt) => (
          <SelectItem key={opt.name} value={opt.name}>
            {opt.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function DateField({
  value,
  onChange,
  inputClassName,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  inputClassName?: string;
}) {
  const dateValue = value ? value.slice(0, 10) : '';
  return (
    <input
      type="date"
      value={dateValue}
      onChange={(e) => onChange(e.target.value || undefined)}
      className={cn(
        'flex h-8 w-full items-center rounded-md border border-input bg-background px-2.5 py-1 text-[13px] ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        inputClassName,
      )}
    />
  );
}

function NumberField({
  value,
  onChange,
  placeholder,
}: {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(value !== undefined ? String(value) : '');

  useEffect(() => {
    setDraft(value !== undefined ? String(value) : '');
  }, [value]);

  return (
    <input
      type="number"
      min={0}
      step={5}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const trimmed = draft.trim();
        if (trimmed === '') {
          if (value !== undefined) onChange(undefined);
          return;
        }
        const parsed = Number(trimmed);
        if (Number.isFinite(parsed) && parsed >= 0 && parsed !== value) {
          onChange(parsed);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(value !== undefined ? String(value) : '');
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
      className="flex h-8 w-full items-center rounded-md border border-input bg-background px-2.5 py-1 text-[13px] ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    />
  );
}

/**
 * AirtableTextField — multi-line text bound to an Airtable field. Saves on
 * blur or ⌘/Ctrl+Enter, restores on Escape. Replaces the Phase B
 * `LocalTextField` (which wrote to localStorage).
 */
function AirtableTextField({
  value,
  placeholder,
  onSave,
  rows = 2,
}: {
  value: string;
  placeholder?: string;
  onSave: (value: string) => void;
  rows?: number;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const dirty = draft !== value;

  return (
    <div className="space-y-1">
      <textarea
        data-task-editable="true"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (dirty) onSave(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDraft(value);
            (e.currentTarget as HTMLTextAreaElement).blur();
          }
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            (e.currentTarget as HTMLTextAreaElement).blur();
          }
        }}
        placeholder={placeholder}
        rows={rows}
        className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
      {dirty && (
        <div className="text-right text-[10px] text-muted-foreground">
          Unsaved · blur or ⌘+Enter to save
        </div>
      )}
    </div>
  );
}

function NotesField({
  value,
  raw,
  onSave,
  onExpandedEdit,
}: {
  value: string;
  raw: boolean;
  onSave: (value: string) => void;
  /** Called on double-click in preview mode — opens the full-drawer editor. */
  onExpandedEdit?: () => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  if (!raw) {
    return (
      <Card
        className="cursor-text px-4 py-3 transition-colors hover:bg-muted/30"
        onDoubleClick={() => onExpandedEdit?.()}
        title="Double-click to edit in full drawer"
      >
        <MarkdownNotes
          notes={value}
          emptyState="No notes yet — double-click here to start writing, or toggle Edit raw / Expand."
        />
      </Card>
    );
  }

  const dirty = draft !== value;

  return (
    <div className="space-y-1">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (dirty) onSave(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDraft(value);
            (e.currentTarget as HTMLTextAreaElement).blur();
          }
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            (e.currentTarget as HTMLTextAreaElement).blur();
          }
        }}
        rows={8}
        placeholder="Markdown notes — supports headings, lists, code, links."
        className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-[12px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
      <div className="text-right text-[10px] text-muted-foreground">
        {dirty ? 'Unsaved · blur or ⌘+Enter to save' : 'Saved'}
      </div>
    </div>
  );
}

// ---------- Client comms (Question for Clients + Send Update) ----------

interface ClientCommsBlockProps {
  task: Task;
  sendStatus:
  | { state: 'idle' }
  | { state: 'sending' }
  | { state: 'sent'; at: number }
  | { state: 'error'; message: string };
  onSaveQuestions: (value: string) => void;
}

function ClientCommsBlock({ task, sendStatus, onSaveQuestions }: ClientCommsBlockProps) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <SectionHeading className="inline-flex items-center gap-1.5">
        <HelpCircle className="h-3 w-3" aria-hidden />
        Client Questions
      </SectionHeading>
      <p className="mb-1.5 text-[11px] text-muted-foreground">
        Questions for the client. If populated, the Send Update email appends a &ldquo;Questions for
        you&rdquo; section. One question per line.
      </p>
      <AirtableTextField
        value={task.questionForClients ?? ''}
        placeholder={
          'e.g. Is the new logo file final, or should we wait?\nShould we keep the old footer copy or replace it?'
        }
        onSave={onSaveQuestions}
        rows={3}
      />
      {sendStatus.state === 'error' && (
        <div className="mt-2 inline-flex items-start gap-1.5 text-[11px] text-destructive">
          <AlertCircle className="mt-px h-3 w-3 flex-shrink-0" aria-hidden />
          <span>{sendStatus.message}</span>
        </div>
      )}
    </div>
  );
}

// ---------- AI Overview card ----------

/**
 * OverviewCard — renders the Airtable AI-generated `Overview` field at the top
 * of the drawer. Hides itself when the AI hasn't produced anything yet, so we
 * don't waste vertical space with an empty placeholder.
 *
 * Shows a small "AI · stale" pill when Airtable flags the field as stale (the
 * underlying inputs changed since the last regeneration, so the summary may
 * be out-of-date).
 */
function OverviewCard({ overview, isStale }: { overview?: string; isStale?: boolean }) {
  const trimmed = overview?.trim();
  if (!trimmed) return null;

  return (
    <Card className="border-l-2 border-l-primary/60 bg-primary/[0.02] px-4 py-3">
      <div className="mb-1.5 flex items-center gap-2">
        <SectionHeading className="mb-0 inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-primary" aria-hidden />
          AI Overview
        </SectionHeading>
        {isStale && (
          <Badge
            variant="warning"
            className="px-1.5 py-0 text-[9px] font-semibold uppercase tracking-[0.5px]"
            title="Underlying inputs have changed since the AI last regenerated this overview."
          >
            stale
          </Badge>
        )}
      </div>
      <MarkdownNotes notes={trimmed} emptyState={null} />
    </Card>
  );
}

// ---------- Expanded notes editor (full-drawer takeover) ----------

interface ExpandedNotesEditorProps {
  taskName: string;
  initialValue: string;
  surface: 'drawer' | 'workspace';
  savePending: boolean;
  onSave: (value: string) => void;
  onCancel: () => void;
}

/**
 * ExpandedNotesEditor — replaces the entire drawer body with a tall textarea
 * so the user has real estate to write.
 *
 * - Esc cancels (reverts and exits expanded mode)
 * - ⌘/Ctrl+Enter saves
 * - Save / Cancel buttons in a sticky footer
 *
 * Triggered by double-clicking the Notes preview, the "Edit Notes" Action
 * Center button, or the Expand link in the Notes section header.
 */
function ExpandedNotesEditor({
  taskName,
  initialValue,
  surface,
  savePending,
  onSave,
  onCancel,
}: ExpandedNotesEditorProps) {
  const [draft, setDraft] = useState(initialValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus the textarea on mount so the user can type immediately.
  useEffect(() => {
    const node = textareaRef.current;
    if (!node) return;
    node.focus();
    // Move caret to end of existing content rather than selecting all.
    const len = node.value.length;
    node.setSelectionRange(len, len);
  }, []);

  const dirty = draft !== initialValue;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        onSave(draft);
      }
    },
    [draft, onCancel, onSave],
  );

  return (
    <div className="flex h-full flex-col">
      {surface === 'workspace' && (
        <div className="border-b border-border px-4 py-4">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="break-words text-[15px] font-semibold leading-tight">
                <span className="text-muted-foreground">Editing notes:</span>{' '}
                <span className="font-medium">{taskName}</span>
              </h2>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Markdown supported. Esc to cancel. Cmd/Ctrl+Enter to save.
              </p>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close editor"
              title="Cancel (Esc)"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      )}
      {surface === 'drawer' && (
      <SheetHeader>
        <div className="flex items-start gap-3 pr-6">
          <div className="min-w-0 flex-1">
            <SheetTitle className="break-words text-[15px]">
              <span className="text-muted-foreground">Editing notes —</span>{' '}
              <span className="font-medium">{taskName}</span>
            </SheetTitle>
            <SheetDescription className="mt-1 text-[11px]">
              Markdown supported · Esc to cancel · ⌘/Ctrl+Enter to save
            </SheetDescription>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Close editor"
            title="Cancel (Esc)"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </SheetHeader>
      )}

      <div className="flex flex-1 flex-col px-5 py-4">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Markdown notes — supports # headings, - lists, **bold**, [links](url), ```code```."
          className="min-h-0 flex-1 resize-none rounded-md border border-input bg-background px-3 py-2.5 font-mono text-[13px] leading-relaxed ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-5 py-3">
        <div className="text-[11px] text-muted-foreground">
          {dirty ? (
            <span className="inline-flex items-center gap-1 text-foreground/70">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
              Unsaved changes
            </span>
          ) : (
            <span>No changes</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!dirty || savePending}
            onClick={() => onSave(draft)}
            className="gap-1.5"
          >
            {savePending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            Save Notes
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Time tab supporting components ----------

function TimeSummary({ task, entriesTotalMinutes }: { task: Task; entriesTotalMinutes: number }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryStat
        label="Estimate"
        value={task.estimateMinutes ? formatDuration(task.estimateMinutes) : '—'}
        hint="Set in Overview"
      />
      <SummaryStat
        label="Tracked total"
        value={formatDuration(task.trackedMinutes ?? entriesTotalMinutes)}
        hint="Across all entries"
      />
      <SummaryStat
        label="This week"
        value={formatDuration(task.weekTrackedMinutes ?? 0)}
        hint="Last 7 days"
      />
      <SummaryStat
        label="With subtasks"
        value={formatDuration(
          task.totalTrackedWithSubtasksMinutes ?? task.trackedMinutes ?? entriesTotalMinutes,
        )}
        hint={`${formatDuration(task.subtaskTrackedMinutes ?? 0)} from subtasks`}
      />
    </div>
  );
}

function SummaryStat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-[18px] font-bold leading-none tabular-nums text-foreground">
        {value}
      </div>
      <div className="mt-1 text-[10px] text-muted-foreground">{hint}</div>
    </div>
  );
}

function TimeEntriesList({ entries }: { entries: TimeEntry[] }) {
  const navigate = useNavigate();

  if (entries.length === 0) {
    return (
      <Card className="px-4 py-6 text-center">
        <p className="text-[13px] font-medium text-foreground">No time tracked yet.</p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Use a Quick Log button above to record time without leaving this drawer.
        </p>
      </Card>
    );
  }

  const total = entries.reduce((sum, e) => sum + (e.durationMinutes ?? 0), 0);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
        <span>{entries.length} entries</span>
        <span>Total {formatDuration(total)}</span>
      </div>
      <ul className="divide-y divide-border">
        {entries.slice(0, 12).map((entry) => (
          <li
            key={entry.id}
            className="cursor-pointer px-3 py-2 transition-colors hover:bg-muted/40 focus-within:bg-muted/40"
            onClick={() => navigate(`/work/time-entries/${entry.id}`)}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <button
                  type="button"
                  className="block max-w-full truncate text-left text-[13px] text-foreground outline-none"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/work/time-entries/${entry.id}`);
                  }}
                >
                  {entry.notes || entry.name || 'Time entry'}
                </button>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {entry.entryDate ? formatShortDate(entry.entryDate) : '—'}
                  {entry.source && entry.source !== 'portal' && (
                    <span className="ml-2 text-muted-foreground/70">via {entry.source}</span>
                  )}
                  {entry.status && (
                    <span className="ml-2 text-muted-foreground/70">· {entry.status}</span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[13px] font-medium tabular-nums text-foreground">
                  {formatDuration(entry.durationMinutes)}
                </div>
                <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
                  {entry.billable ? (
                    <Badge variant="success" className="px-1.5 py-0 text-[9px]">
                      Billable
                    </Badge>
                  ) : (
                    <Badge variant="muted" className="px-1.5 py-0 text-[9px]">
                      Non-billable
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
        {entries.length > 12 && (
          <li className="bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
            +{entries.length - 12} more entries
          </li>
        )}
      </ul>
    </Card>
  );
}

// ---------- Linked records ----------

function LinkedRecords({ task }: { task: Task }) {
  const [clientDrawerId, setClientDrawerId] = useState<string>();
  const [websiteDrawerId, setWebsiteDrawerId] = useState<string>();
  const hasInvoices = Boolean(task.invoiceIds && task.invoiceIds.length > 0);

  return (
    <>
      <Card>
        <ul className="divide-y divide-border">
          <TaskLinkEditor task={task} kind="client" onOpen={setClientDrawerId} />
          <TaskLinkEditor task={task} kind="website" onOpen={setWebsiteDrawerId} />
          {hasInvoices && (
            <li className="flex items-center gap-3 px-3 py-2 text-[13px]">
              <ExternalLink className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                Invoices
              </span>
              <span className="ml-auto text-foreground">{task.invoiceIds!.length} linked</span>
            </li>
          )}
        </ul>
      </Card>
      {clientDrawerId && (
        <ClientDrawer
          clientId={clientDrawerId}
          onClose={() => setClientDrawerId(undefined)}
          onOpenWebsite={setWebsiteDrawerId}
        />
      )}
      {websiteDrawerId && (
        <WebsiteDrawer
          websiteId={websiteDrawerId}
          onClose={() => setWebsiteDrawerId(undefined)}
          onOpenClient={setClientDrawerId}
        />
      )}
    </>
  );
}

/**
 * Inline link editor for a task's single-link records (Organization, Website).
 * Search + pick writes through PATCH /api/tasks/:id (clientId / websiteId);
 * the same backend Alice uses. Clearing sends null. On success the task detail
 * + list queries are invalidated so the drawer reflects the new link.
 */
function TaskLinkEditor({
  task,
  kind,
  onOpen,
}: {
  task: Task;
  kind: 'client' | 'website';
  onOpen: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');

  const mutation = useMutation({
    mutationFn: (id: string | null) =>
      tasksApi.patch(task.id, kind === 'client' ? { clientId: id } : { websiteId: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TASK_DETAIL_KEY(task.id) });
      queryClient.invalidateQueries({ queryKey: TASKS_LIST_KEY });
      setEditing(false);
      setSearch('');
    },
  });

  const clientsQuery = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => clientsApi.list(),
    enabled: editing && kind === 'client',
  });
  const websitesQuery = useQuery({
    queryKey: ['websites', 'all'],
    queryFn: () => websitesApi.list(),
    enabled: editing && kind === 'website',
  });

  const Icon = kind === 'client' ? Building2 : Globe;
  const label = kind === 'client' ? 'Client' : 'Website';
  const currentId = kind === 'client' ? task.client?.id : task.website?.id;
  const currentLabel =
    kind === 'client'
      ? task.client?.name ?? task.client?.id
      : task.website?.friendlyName ?? task.website?.url ?? task.website?.id;

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (kind === 'client') {
      return (clientsQuery.data?.items ?? [])
        .filter((c) => !q || c.name.toLowerCase().includes(q))
        .slice(0, 8)
        .map((c) => ({ id: c.id, label: c.name, secondary: c.status }));
    }
    return (websitesQuery.data?.items ?? [])
      .filter(
        (w) =>
          !q ||
          (w.friendlyName ?? '').toLowerCase().includes(q) ||
          (w.url ?? '').toLowerCase().includes(q),
      )
      .slice(0, 8)
      .map((w) => ({ id: w.id, label: w.friendlyName ?? w.url, secondary: w.url }));
  }, [kind, search, clientsQuery.data, websitesQuery.data]);

  const busy = mutation.isPending;
  const loadingOptions = kind === 'client' ? clientsQuery.isLoading : websitesQuery.isLoading;

  return (
    <li className="px-3 py-2 text-[13px]">
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
          {label}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {currentId ? (
            <button
              type="button"
              onClick={() => onOpen(currentId)}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {currentLabel}
              <ExternalLink className="h-3 w-3" aria-hidden />
            </button>
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            disabled={busy}
            className="text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {editing ? 'Cancel' : currentId ? 'Change' : 'Link'}
          </button>
          {currentId && !editing && (
            <button
              type="button"
              onClick={() => mutation.mutate(null)}
              disabled={busy}
              className="text-[11px] text-muted-foreground hover:text-destructive disabled:opacity-50"
            >
              Clear
            </button>
          )}
          {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden />}
        </span>
      </div>
      {editing && (
        <div className="mt-2 space-y-1.5">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={kind === 'client' ? 'Search organizations…' : 'Search websites…'}
            className="h-8 text-[13px]"
            autoFocus
          />
          <div className="max-h-56 overflow-y-auto rounded-md border border-border">
            {loadingOptions ? (
              <div className="flex items-center gap-2 px-3 py-2 text-[12px] text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Loading…
              </div>
            ) : results.length === 0 ? (
              <div className="px-3 py-2 text-[12px] text-muted-foreground">No matches.</div>
            ) : (
              results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  disabled={busy || r.id === currentId}
                  onClick={() => mutation.mutate(r.id)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12px] hover:bg-muted/60 disabled:cursor-default',
                    r.id === currentId && 'opacity-50',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate text-foreground">{r.label}</span>
                  {r.secondary && (
                    <span className="max-w-[45%] truncate text-[11px] text-muted-foreground">
                      {r.secondary}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function TaskLinkedEmails({ taskId }: { taskId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['communications', 'task-linked-emails', taskId],
    queryFn: () =>
      communicationsApi.list({
        taskId,
        source: 'gmail',
        limit: 12,
      }),
    enabled: Boolean(taskId),
    staleTime: 30_000,
  });

  if (isLoading) return <Card className="h-24 animate-pulse" />;

  if (error) {
    return (
      <Card className="flex items-start gap-3 px-4 py-3 text-[12px] text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
        <span>{error instanceof Error ? error.message : 'Could not load linked emails.'}</span>
      </Card>
    );
  }

  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <Card className="flex items-center gap-3 px-4 py-3 text-[12px] text-muted-foreground">
        <Mail className="h-4 w-4 flex-shrink-0" aria-hidden />
        <span>No Gmail threads linked to this task yet.</span>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-border">
        {items.map((item) => (
          <TaskLinkedEmailRow key={item.id} item={item} />
        ))}
      </ul>
    </Card>
  );
}

function TaskLinkedEmailRow({ item }: { item: CommunicationTimelineItem }) {
  const navigate = useNavigate();
  const openEmailComposer = useUiStore((state) => state.openEmailComposer);
  const gmailThreadId =
    typeof item.metadata.gmailThreadId === 'string' ? item.metadata.gmailThreadId : undefined;

  return (
    <li className="px-3 py-2.5">
      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="truncate text-[13px] font-medium text-foreground">{item.title}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                {item.contactEmail && <span>{item.contactEmail}</span>}
                {item.clientName && <span>{item.clientName}</span>}
                {item.status && <Badge variant="muted">{formatActivityAction(item.status)}</Badge>}
              </div>
            </div>
            <time className="whitespace-nowrap text-[11px] text-muted-foreground">
              {new Date(item.occurredAt).toLocaleString()}
            </time>
          </div>
          {item.summary && (
            <p className="mt-1 line-clamp-2 text-[12px] text-muted-foreground">
              {cleanEmailSummary(item.summary)}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {item.href && (
              <Button asChild type="button" size="sm" variant="outline" className="h-7 gap-1.5">
                <a href={item.href} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  Open Gmail
                </a>
              </Button>
            )}
            {item.recordId && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 gap-1.5"
                onClick={() =>
                  navigate(`/knowledge/quick-notes?communicationId=${encodeURIComponent(item.recordId!)}`)
                }
              >
                <Link2 className="h-3.5 w-3.5" aria-hidden />
                Edit Link
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 gap-1.5"
              onClick={() =>
                openEmailComposer({
                  mode: 'reply',
                  replyToRecordId: item.recordId,
                  threadId: gmailThreadId,
                  subject: item.title,
                  toEmails: item.contactEmail ? [item.contactEmail] : [],
                  clientId: item.clientId,
                  clientName: item.clientName,
                  userId: item.userId,
                  userName: item.userName,
                  taskId: item.taskId,
                  taskName: item.title,
                })
              }
              disabled={!item.recordId && !gmailThreadId}
            >
              <Reply className="h-3.5 w-3.5" aria-hidden />
              Reply
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
}

// ---------- Misc ----------

function SectionHeading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-1.5 text-[10px] font-semibold uppercase tracking-[0.7px] text-muted-foreground',
        className,
      )}
    >
      {children}
    </div>
  );
}

function TaskActivityFeed({ taskId }: { taskId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: TASK_ACTIVITY_KEY(taskId),
    queryFn: () => activityApi.list({ entityType: 'task', entityId: taskId, limit: 50 }),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return <Card className="h-28 animate-pulse" />;
  }

  if (error) {
    return (
      <Card className="flex items-start gap-3 px-4 py-3 text-[12px] text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
        <span>{error instanceof Error ? error.message : 'Could not load activity.'}</span>
      </Card>
    );
  }

  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <Card className="flex items-center gap-3 px-4 py-3 text-[12px] text-muted-foreground">
        <History className="h-4 w-4 flex-shrink-0" aria-hidden />
        <span>No activity logged for this task yet.</span>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-border">
        {items.map((row) => (
          <li key={row.id} className="flex items-start gap-3 px-3 py-2.5">
            <History className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-[13px]">
                <span className="font-medium">{row.actorName}</span>{' '}
                <span className="text-muted-foreground">{formatActivityAction(row.action)}</span>
                {row.entityLabel && <span className="font-medium"> {row.entityLabel}</span>}
              </div>
              {row.notes && (
                <div className="mt-0.5 text-[12px] text-muted-foreground">{row.notes}</div>
              )}
              {row.source && row.source !== 'portal' && (
                <Badge variant="muted" className="mt-1 px-1.5 py-0 text-[9px]">
                  {row.source}
                </Badge>
              )}
            </div>
            <time className="whitespace-nowrap text-[11px] text-muted-foreground">
              {new Date(row.createdAt).toLocaleString()}
            </time>
          </li>
        ))}
      </ul>
    </Card>
  );
}


// ---------- Skeleton + error states ----------

function DrawerSkeleton() {
  return (
    <>
      <div className="border-b border-border px-5 py-4">
        <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-3 w-1/3 animate-pulse rounded bg-muted/70" />
      </div>
      <div className="space-y-4 p-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <div className="h-3 w-24 animate-pulse rounded bg-muted/70" />
            <div className="h-8 animate-pulse rounded bg-muted/40" />
          </div>
        ))}
      </div>
    </>
  );
}

function DrawerError({ error, onClose }: { error: unknown; onClose: () => void }) {
  const message = error instanceof Error ? error.message : 'Unknown error';
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <AlertCircle className="h-8 w-8 text-destructive" aria-hidden />
      <h2 className="text-base font-semibold text-foreground">Couldn't load that task.</h2>
      <p className="max-w-sm text-[13px] text-muted-foreground">{message}</p>
      <Button onClick={onClose} variant="outline" size="sm">
        Back to list
      </Button>
    </div>
  );
}

// ---------- Helpers ----------

function appendContextDump(existingNotes: string, dump: string, files: string[]): string {
  const lines = [
    existingNotes.trim(),
    '',
    `## Context Dump - ${new Date().toLocaleString()}`,
    dump,
    files.length > 0 ? `Files noted: ${files.join(', ')}` : '',
  ].filter(Boolean);
  return lines.join('\n');
}

function mergeTaskPatch(task: Task, patch: Partial<Task>): Task {
  return {
    ...task,
    ...patch,
  };
}

function formatActivityAction(action: string): string {
  return action.replaceAll('_', ' ');
}

function cleanEmailSummary(value: string): string {
  const quote = value
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line.startsWith('>'));
  if (quote) return quote.replace(/^>\s*/, '');
  return value
    .split('\n')
    .map((line) => line.replace(/^#\s+/, '').replace(/^-\s+/, '').trim())
    .filter(Boolean)
    .slice(0, 3)
    .join(' · ');
}

function actionForPatch(patch: Partial<Task>): string | undefined {
  if (patch.status !== undefined) return 'status_changed';
  if (patch.priority !== undefined) return 'priority_changed';
  if (patch.notes !== undefined) return 'notes_updated';
  if (patch.targetDate !== undefined) return 'target_date_changed';
  if (patch.startDate !== undefined) return 'start_date_changed';
  if (patch.billStatus !== undefined) return 'bill_status_changed';
  if (patch.finalReview !== undefined) return 'final_review_changed';
  if (patch.estimateMinutes !== undefined) return 'estimate_changed';
  if (patch.nextAction !== undefined) return 'next_action_updated';
  if (patch.definitionOfDone !== undefined) return 'dod_updated';
  if (patch.questionForClients !== undefined) return 'questions_updated';
  if (patch.commandCenterFlags !== undefined) return 'command_center_flags_updated';
  if (patch.name !== undefined) return 'name_changed';
  return 'updated';
}
